/**
 * IntegrationsContent — Account hub tab body for managing integrations.
 *
 * Two sub-tabs (ADR 102 integrations surface):
 *   - Sources: the shared <SourcesPage /> from `@tiaki/sources-ui` via the SMS
 *     host adapter. The host exposes the full sites list via `useSitesList`,
 *     putting <SourcesPage /> into multi-site mode (one collapsible section per
 *     site).
 *   - IPAWS: district-level IPAWS-OPEN origination config (ADR-139 D4).
 *     Unlike sources, IPAWS config is per-ORGANIZATION, not
 *     per-site, so it gets its own surface rather than a source template. The
 *     sub-tab only renders for admins holding `schools.view_ipawsconfig`
 *     (ADR-123 disposition B — granted to no built-in role) AND when the
 *     org's `ipaws_origination` feature is on (entitlement gate:
 *     release flag ∧ licensed module, computed server-side by has_feature()).
 *
 * Account.tsx provides the page chrome (header, top-level tab menu); this
 * component renders the nested sub-tab menu + content, mirroring
 * AuthorizationContent's `subtab` URL-param pattern so deep links work
 * (`/account?tab=integrations&subtab=ipaws`).
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { TabMenu, type TabItem } from '@themes/aegix';
import { SourcesPage, SourcesProvider } from '@tiaki/sources-ui';

import { useSites } from '../../hooks/queries/useSites';
import type { Site } from '../../services/sites.service';
import { useSmsSourcesHost } from '../../pages/integrations/useSmsSourcesHost';
import { useFeature } from '../../hooks/useFeature';
import { FeatureCode } from '../../types/features';
import { useHasPermissions } from '../../utils/hasPermission';
import { IpawsConfigContent } from './IpawsConfigContent';

interface IntegrationsContentProps {
  initialSubTab?: string;
}

function SourcesContent() {
  const { t } = useTranslation();
  const sitesQuery = useSites({ limit: 200 });
  const sites: Site[] = useMemo(
    () => sitesQuery.data?.results ?? [],
    [sitesQuery.data],
  );

  const host = useSmsSourcesHost({ sites });

  return (
    <Flex direction="column" flex="1" minHeight={0} px={8} pb={8}>
      {sites.length > 0 ? (
        <SourcesProvider host={host}>
          <SourcesPage />
        </SourcesProvider>
      ) : (
        <Box
          bg="surface.primary"
          borderRadius="md"
          p={6}
          borderWidth="1px"
          borderColor="border.primary"
          mt={4}
        >
          <Text>
            {sitesQuery.isLoading
              ? t('integrations.loadingSites', 'Loading sites…')
              : t(
                  'integrations.noSitesAvailable',
                  'No sites available — integrations are scoped per site.',
                )}
          </Text>
        </Box>
      )}
    </Flex>
  );
}

export function IntegrationsContent({
  initialSubTab,
}: IntegrationsContentProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewIpaws = useHasPermissions(['schools.view_ipawsconfig']);
  // The IPAWS surface is additionally entitlement-gated on the
  // ipaws_origination feature (release flag ∧ licensed module, ADR-046) —
  // the backend computes flag ∧ license in has_feature() and the config API
  // answers as-if-missing when dark, so this hides the sub-tab for
  // unentitled orgs. Permission AND feature, mirroring the server.
  const ipawsFeatureEnabled = useFeature(FeatureCode.IPAWS_ORIGINATION);
  const showIpaws = canViewIpaws && ipawsFeatureEnabled;

  const tabs: TabItem[] = useMemo(
    () => [
      {
        value: 'sources',
        label: t('integrations.sources', 'Sources'),
        'data-e2e': 'tab-sources',
      },
      ...(showIpaws
        ? [
            {
              value: 'ipaws',
              label: t('integrations.ipaws', 'IPAWS'),
              'data-e2e': 'tab-ipaws',
            },
          ]
        : []),
    ],
    [showIpaws, t],
  );

  const requestedTab =
    searchParams.get('subtab')?.toLowerCase().replace(/\s+/g, '-') ||
    initialSubTab?.toLowerCase().replace(/\s+/g, '-') ||
    'sources';
  let activeTab = requestedTab;
  if (activeTab === 'ipaws' && !showIpaws) activeTab = 'sources';
  if (!tabs.some((t) => t.value === activeTab)) activeTab = 'sources';

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('subtab', tab);
      next.delete('id');
      next.delete('public_id');
      return next;
    });
  };

  return (
    <Flex direction="column" flex="1" minHeight={0}>
      <TabMenu
        tabs={tabs}
        activeTab={activeTab}
        onChange={handleTabChange}
        e2eTitle="IntegrationsSubTabs"
      />
      {activeTab === 'sources' && <SourcesContent />}
      {activeTab === 'ipaws' && showIpaws && <IpawsConfigContent />}
    </Flex>
  );
}
